import {ContentProvider, DEFAULT_REPORT_TIMEOUT_MS} from '../domain/content_provider'
import { PageContainer,EndMarker,ReportPrinter,EndDetector } from '../domain/page'

function createBatches(totalElements: number, batchSize: number): [number, number][] {
    const batches: [number, number][] = [];
    let start = 0;

    while (start < totalElements) {
        const end = Math.min(start + batchSize, totalElements);
        batches.push([start, end - 1]); // start is inclusive, end is exclusive, hence `end - 1`
        start += batchSize;
    }

    return batches;
}

/**
 * Creates a promise that rejects after the specified timeout.
 * Used as a safety mechanism to prevent indefinite hangs during report generation.
 */
function createTimeoutPromise(timeoutMs: number): Promise<never> {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`Report generation timed out after ${timeoutMs}ms`))
        }, timeoutMs)
    })
}
export class ReportEngine {
    content_provider:ContentProvider
    page_container:PageContainer
    end_marker:EndMarker
    report_printer:ReportPrinter
    end_detector:EndDetector
    pagesPerBatch:number
    constructor(content_provider:ContentProvider, page_container:PageContainer, end_detector:EndDetector,end_marker:EndMarker, report_printer:ReportPrinter, pagesPerBatch:number) {
        this.content_provider = content_provider
        this.page_container = page_container
        this.end_marker = end_marker
        this.report_printer = report_printer
        this.end_detector = end_detector
        this.pagesPerBatch = pagesPerBatch
    }

    /**
     * Get the timeout for report generation from the content provider.
     * Falls back to DEFAULT_REPORT_TIMEOUT_MS if not specified.
     */
    private getTimeoutMs(): number {
        if (this.content_provider.getTimeoutMs) {
            return this.content_provider.getTimeoutMs()
        }
        return DEFAULT_REPORT_TIMEOUT_MS
    }

    /**
     * Internal method that performs the actual report generation.
     * Separated from generateReport() to allow timeout wrapping.
     */
    private async doGenerateReport(): Promise<void> {
        this.end_marker.disable()
        const page_generators = await this.content_provider.getContent()
        let  nextPage=0;
        for (const page_generator of page_generators) {
            console.log("next Section")
            let page = await page_generator.getInitialPage()
            await page.setVisible(true)
            await page.setPageNum(nextPage)
            nextPage++
            await this.page_container.addPage(page)
            console.log(`next Page ${nextPage}`)
            while (await page_generator.canGrow(page)){
                await page_generator.grow(page)
                await this.page_container.refreshLastPage()
                if(page.renderer && await page.renderer.isOverflown()){
                    await page_generator.shrink(page)
                    await page.setVisible(false)
                    //await this.page_container.refreshLastPage()
                    page = await page_generator.getNextPage(page)
                    await page.setVisible(true)
                    await page.setPageNum(nextPage)
                    nextPage++
                    await this.page_container.addPage(page)
                    console.log(`next Page ${nextPage}`)
                }
            }
            await page.setVisible(false)
            //await this.page_container.refreshLastPage()
        }
        //await this.page_container.refreshLastPage()
        //await this.page_container.refreshAllPages()
        await this.end_detector.waitStillIsReady()
    }

    async generateReport(): Promise<void> {
        const timeoutMs = this.getTimeoutMs()
        try {
            // Race between report generation and timeout
            // If timeout wins, we still enable end marker in finally block
            await Promise.race([
                this.doGenerateReport(),
                createTimeoutPromise(timeoutMs)
            ])
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            if (errorMessage.includes('timed out')) {
                console.error(`Report generation timed out after ${timeoutMs}ms - enabling end marker anyway`)
            } else {
                console.error('Report generation failed:', error)
            }
        } finally {
            // CRITICAL: Always enable end marker to prevent backend thread from hanging
            // Even if report generation fails or times out, we must signal completion to avoid zombie threads
            this.end_marker.enable()
        }

        // Printing phase - runs after end_marker is enabled
        try {
            const numPages=this.page_container.getNumPages()
            if (numPages === 0) {
                console.warn('No pages generated, skipping print phase')
                return
            }
            const batches = createBatches(numPages, this.pagesPerBatch);
            let index=0;
            for (const [start, end] of batches) {
                await this.page_container.setVisibleRange(start, end);
                //await this.page_container.refreshAllPages();
                console.log(`Printing pages ${start} to ${end}`);
                console.log(this.page_container.getAllPages().map(p=>p.visible))
                await this.end_detector.waitStillIsReady() // Wait until all images are loaded
                await this.report_printer.print(index, batches.length);
                index++
            }
        } catch (error) {
            console.error('Report printing failed:', error)
        }
    }
}